
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCrawler } from '@/hooks/useCrawler';
import { logComponentError } from '@/utils/logger';

export function useDashboardState() {
  const location = useLocation();
  const {
    isAnalyzing,
    progress,
    crawlResult,
    websiteUrl,
    setCrawlResult,
    handleSubmit,
    handleCancel,
    compareWithHistory,
    clearComparison,
    compareMode,
    comparisonResult
  } = useCrawler();

  const [activePage, setActivePage] = useState<string>("dashboard");
  const [selectedPageUrl, setSelectedPageUrl] = useState<string | null>(null);
  const [hasLoadedFromState, setHasLoadedFromState] = useState<boolean>(false);

  const handleNewCrawl = () => {
    setCrawlResult(null);
    setSelectedPageUrl(null);
  };

  const handleNavigate = (page: string) => {
    setActivePage(page);
    
    if (page !== 'dashboard') {
      window.history.pushState(null, '', `#${page}`);
    } else {
      window.history.pushState(null, '', window.location.pathname);
    }
    
    scrollToSection(page);
  };

  const handleSelectPage = (url: string) => {
    setSelectedPageUrl(url);
    setActivePage('deep-analysis');
    window.history.pushState(null, '', `#deep-analysis`);
    
    // Auto-scroll to deep analysis section
    scrollToSection('deep-analysis');
  };

  const scrollToSection = (sectionId: string) => {
    // Wait a bit to ensure the section is rendered
    setTimeout(() => {
      try {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (error) {
        logComponentError('Dashboard', error as Error, { action: 'scrollToSection', sectionId });
      }
    }, 100);
  };

  return {
    isAnalyzing,
    progress,
    crawlResult,
    websiteUrl,
    activePage,
    selectedPageUrl,
    hasLoadedFromState,
    setHasLoadedFromState,
    handleSubmit,
    handleCancel,
    handleNewCrawl,
    handleNavigate,
    handleSelectPage,
    compareWithHistory,
    clearComparison,
    compareMode,
    comparisonResult,
    scrollToSection,
    location
  };
}
